import { useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import Grid from "@mui/material/Grid";
import { Card } from "@mui/material";
import * as XLSX from "xlsx";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import {
  IoCloudUploadOutline, IoDocumentTextOutline, IoMapOutline,
  IoCheckmarkCircle, IoCloseCircle, IoWarningOutline,
} from "react-icons/io5";

import { useFinOps } from "context/FinOpsContext";
import { SAMPLE_TB } from "data/finopsData";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

function DropZone({ label, subtitle, icon: Icon, dragging, onDragOver, onDragLeave, onDrop, onClick, fileRef, onFileChange, loaded, accent }) {
  return (
    <VuiBox
      onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} onClick={onClick}
      sx={{
        border: `2px dashed ${dragging ? accent : loaded ? accent : "#56577A"}`,
        borderRadius: "16px", p: 3, textAlign: "center", cursor: "pointer",
        background: loaded ? `${accent}15` : dragging ? `${accent}12` : "rgba(255,255,255,0.02)",
        transition: "all 0.2s", minHeight: "130px",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      }}
    >
      <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={onFileChange} />
      <Icon size="32px" color={loaded ? accent : dragging ? accent : "#56577A"} />
      <VuiTypography variant="h6" color="white" mt={1} mb={0.5} fontSize="14px">
        {loaded ? `✓ ${loaded}` : label}
      </VuiTypography>
      <VuiTypography variant="caption" color="text">{subtitle}</VuiTypography>
    </VuiBox>
  );
}

function StatBadge({ label, value, color }) {
  return (
    <VuiBox px={2} py={1.5} sx={{ background: `${color}15`, borderRadius: "10px", border: `1px solid ${color}30`, textAlign: "center", flex: 1 }}>
      <VuiTypography variant="caption" color="text" display="block">{label}</VuiTypography>
      <VuiTypography variant="button" fontWeight="bold" sx={{ color }}>{value}</VuiTypography>
    </VuiBox>
  );
}

function parseMapping(raw) {
  return raw.map((r) => ({
    account:        String(r.Account        ?? r.account        ?? "").trim(),
    name:           String(r.Account_Name   ?? r.AccountName    ?? r.Name ?? "").trim(),
    statement:      String(r.Statement      ?? r.statement      ?? "").trim().toUpperCase(),
    lineItem:       String(r.LineItem       ?? r.Line_Item      ?? r.lineItem ?? "").trim(),
    classification: String(r.Classification ?? r.classification ?? "").trim(),
    group:          String(r.Group          ?? r.group          ?? "").trim(),
  })).filter((r) => r.account && r.lineItem);
}

function TBPreview({ rows }) {
  if (!rows || rows.length === 0) return null;
  const preview = rows.slice(0, 8);
  const totalDr = rows.reduce((s, r) => s + (r.debit || 0), 0);
  const totalCr = rows.reduce((s, r) => s + (r.credit || 0), 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.01;
  const fmt = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <VuiBox mt={2}>
      {/* Quick stats */}
      <VuiBox display="flex" gap={1.5} mb={2} flexWrap="wrap">
        <StatBadge label="Accounts" value={rows.length} color="#0075FF" />
        <StatBadge label="Total Debit" value={`AED ${fmt(totalDr)}`} color="#4FD1C5" />
        <StatBadge label="Total Credit" value={`AED ${fmt(totalCr)}`} color="#C9A84C" />
        <VuiBox px={2} py={1.5} sx={{
          background: balanced ? "rgba(72,187,120,0.15)" : "rgba(252,129,129,0.15)",
          borderRadius: "10px",
          border: `1px solid ${balanced ? "#48BB7840" : "#FC818140"}`,
          textAlign: "center", flex: 1,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        }}>
          <VuiTypography variant="caption" color="text" display="block">Balance</VuiTypography>
          <VuiBox display="flex" alignItems="center" gap="4px">
            {balanced
              ? <IoCheckmarkCircle color="#48BB78" size="14px" />
              : <IoCloseCircle color="#FC8181" size="14px" />}
            <VuiTypography variant="button" fontWeight="bold" sx={{ color: balanced ? "#48BB78" : "#FC8181" }}>
              {balanced ? "Balanced" : `Off by AED ${fmt(Math.abs(totalDr - totalCr))}`}
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      </VuiBox>

      {/* Preview table */}
      <VuiTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>
        PREVIEW — first {preview.length} of {rows.length} accounts
      </VuiTypography>
      <VuiBox sx={{ overflowX: "auto", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.08)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
          <thead>
            <tr style={{ background: "rgba(0,117,255,0.15)" }}>
              {["Account", "Name", "Debit", "Credit", "Closing"].map((h) => (
                <th key={h} style={{ padding: "8px 10px", textAlign: h === "Account" || h === "Name" ? "left" : "right", color: "#A0AEC0", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.map((r, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                <td style={{ padding: "7px 10px", color: "#E2E8F0", fontFamily: "monospace" }}>{r.account}</td>
                <td style={{ padding: "7px 10px", color: "#A0AEC0", maxWidth: "140px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.accountName || "—"}</td>
                <td style={{ padding: "7px 10px", color: "#4FD1C5", textAlign: "right" }}>{fmt(r.debit)}</td>
                <td style={{ padding: "7px 10px", color: "#C9A84C", textAlign: "right" }}>{fmt(r.credit)}</td>
                <td style={{ padding: "7px 10px", color: "#E2E8F0", textAlign: "right" }}>{fmt(r.closing)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </VuiBox>
    </VuiBox>
  );
}

function MappingPreview({ mapping }) {
  if (!mapping || mapping.length === 0) return null;
  const byStmt = mapping.reduce((acc, r) => {
    const s = r.statement || "OTHER";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const colors = { BS: "#0075FF", "P&L": "#48BB78", PL: "#48BB78", IS: "#48BB78", OTHER: "#56577A" };

  return (
    <VuiBox mt={2} p={2} sx={{ background: "rgba(201,168,76,0.06)", borderRadius: "10px", border: "1px solid rgba(201,168,76,0.2)" }}>
      <VuiTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1.5}>
        MAPPING SUMMARY — {mapping.length} rules loaded
      </VuiTypography>
      <VuiBox display="flex" gap={1.5} flexWrap="wrap">
        {Object.entries(byStmt).map(([stmt, count]) => (
          <VuiBox key={stmt} px={2} py={1} sx={{ background: `${colors[stmt] || "#56577A"}20`, borderRadius: "8px", border: `1px solid ${colors[stmt] || "#56577A"}40` }}>
            <VuiTypography variant="caption" fontWeight="bold" sx={{ color: colors[stmt] || "#56577A" }}>
              {stmt}: {count} accounts
            </VuiTypography>
          </VuiBox>
        ))}
      </VuiBox>
    </VuiBox>
  );
}

function UploadTB() {
  const { loadTB, company, period } = useFinOps();
  const history = useHistory();

  const [tbDrag, setTbDrag]               = useState(false);
  const [mapDrag, setMapDrag]             = useState(false);
  const [error, setError]                 = useState("");
  const [pendingRows, setPendingRows]     = useState(null);
  const [pendingMapping, setPendingMapping] = useState(null);
  const [mappingName, setMappingName]     = useState("");
  const [tbFile, setTbFile]               = useState(null);
  const [mapFile, setMapFile]             = useState(null);
  const [downloading, setDownloading]     = useState(false);
  const [apiStatus, setApiStatus]         = useState("");
  const tbRef  = useRef();
  const mapRef = useRef();

  function readXlsx(file, onParsed) {
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) { setError("Upload .xlsx, .xls, or .csv"); return; }
    setError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb  = XLSX.read(e.target.result, { type: "array" });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!raw.length) { setError("File is empty."); return; }
        onParsed(raw, file.name);
      } catch { setError("Could not read file."); }
    };
    reader.readAsArrayBuffer(file);
  }

  function handleTB(file) {
    setTbFile(file);
    readXlsx(file, (raw) => {
      const rows = raw.map((r) => ({
        account:     String(r.Account      ?? r.account      ?? "").trim(),
        accountName: String(r.Account_Name ?? r.AccountName  ?? r.Description ?? "").trim(),
        opening: Number(r.Opening_Balance ?? r.Opening ?? 0),
        debit:   Number(r.Debit   ?? r.debit   ?? 0),
        credit:  Number(r.Credit  ?? r.credit  ?? 0),
        closing: Number(r.Closing_Balance ?? r.Closing ?? 0),
      })).filter((r) => r.account !== "");

      // Auto-detect entity name from Entity column
      const entityName = raw[0]?.Entity ?? raw[0]?.entity ?? null;
      if (entityName) loadTB(rows, String(entityName).trim(), undefined, undefined);

      setPendingRows(rows);
    });
  }

  function handleMappingFile(file) {
    setMapFile(file);
    readXlsx(file, (raw, name) => {
      const mapping = parseMapping(raw);
      if (!mapping.length) { setError("Mapping file has no valid rows. Need: Account, Statement, LineItem, Group columns."); return; }
      setPendingMapping(mapping);
      setMappingName(name);
    });
  }

  function handleViewStatements() {
    loadTB(pendingRows, undefined, undefined, pendingMapping || undefined);
    history.push("/statements");
  }

  function handleLoadSample() {
    loadTB(SAMPLE_TB, "Demo Company", "Year Ended 31 December 2025", null);
    history.push("/statements");
  }

  async function handleDownloadReport() {
    if (!tbFile || !mapFile) { setApiStatus("error:Upload both TB and Mapping files first."); return; }
    setDownloading(true); setApiStatus("");
    try {
      const form = new FormData();
      form.append("tb",      tbFile);
      form.append("mapping", mapFile);
      form.append("company", company);
      form.append("period",  period);
      const res = await fetch(`${API_URL}/generate`, { method: "POST", body: form });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Server error: ${res.status}`);
      }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = "Financial_Statements.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
      setApiStatus("success:Report downloaded — Cover, BS, P&L, Notes 1–24, TB Source.");
    } catch (err) {
      setApiStatus(`error:${err.message}`);
    } finally {
      setDownloading(false);
    }
  }

  // Download sample template
  function handleDownloadTemplate() {
    const wb = XLSX.utils.book_new();
    const tbData = [
      { Account: "1001", Account_Name: "Cash & Bank", Opening_Balance: 100000, Debit: 500000, Credit: 450000, Closing_Balance: 150000 },
      { Account: "1002", Account_Name: "Accounts Receivable", Opening_Balance: 80000, Debit: 200000, Credit: 180000, Closing_Balance: 100000 },
      { Account: "2001", Account_Name: "Accounts Payable", Opening_Balance: 50000, Debit: 120000, Credit: 150000, Closing_Balance: 80000 },
      { Account: "3001", Account_Name: "Share Capital", Opening_Balance: 500000, Debit: 0, Credit: 0, Closing_Balance: 500000 },
      { Account: "4001", Account_Name: "Revenue", Opening_Balance: 0, Debit: 0, Credit: 630000, Closing_Balance: 630000 },
    ];
    const ws = XLSX.utils.json_to_sheet(tbData);
    XLSX.utils.book_append_sheet(wb, ws, "Trial Balance");
    XLSX.writeFile(wb, "TB_Template.xlsx");
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <VuiTypography variant="h4" color="white" fontWeight="bold">Upload Trial Balance ✓</VuiTypography>
          <VuiTypography variant="button" color="text">
            Upload TB + Mapping → instant live statements OR download board-ready Excel report.
          </VuiTypography>
        </VuiBox>

        <Grid container spacing={3}>

          {/* ── Left: Upload panel ── */}
          <Grid item xs={12} lg={5}>
            <Card sx={{ height: "100%" }}>
              <VuiBox display="flex" flexDirection="column" gap={2}>

                {/* Step 1 */}
                <VuiBox display="flex" alignItems="center" gap={1}>
                  <VuiBox sx={{ width: "24px", height: "24px", borderRadius: "50%", background: "#0075FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <VuiTypography variant="caption" color="white" fontWeight="bold">1</VuiTypography>
                  </VuiBox>
                  <VuiTypography variant="caption" color="text" fontWeight="bold">
                    TRIAL BALANCE FILE <span style={{ color: "#FC8181" }}>*required</span>
                  </VuiTypography>
                </VuiBox>
                <DropZone
                  label="Drop your Trial Balance here"
                  subtitle="SAP · Oracle · Tally · QuickBooks · .xlsx .xls .csv"
                  icon={IoCloudUploadOutline}
                  dragging={tbDrag}
                  loaded={pendingRows ? `${pendingRows.length} accounts loaded` : ""}
                  accent="#0075FF"
                  onDragOver={(e) => { e.preventDefault(); setTbDrag(true); }}
                  onDragLeave={() => setTbDrag(false)}
                  onDrop={(e) => { e.preventDefault(); setTbDrag(false); handleTB(e.dataTransfer.files[0]); }}
                  onClick={() => tbRef.current.click()}
                  fileRef={tbRef}
                  onFileChange={(e) => handleTB(e.target.files[0])}
                />

                {/* TB preview */}
                {pendingRows && <TBPreview rows={pendingRows} />}

                {/* Step 2 */}
                <VuiBox display="flex" alignItems="center" gap={1} mt={1}>
                  <VuiBox sx={{ width: "24px", height: "24px", borderRadius: "50%", background: "#C9A84C", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <VuiTypography variant="caption" color="white" fontWeight="bold">2</VuiTypography>
                  </VuiBox>
                  <VuiTypography variant="caption" color="text" fontWeight="bold">
                    MAPPING FILE <span style={{ color: "#a0aec0" }}>required for Excel report</span>
                  </VuiTypography>
                </VuiBox>
                <DropZone
                  label="Drop your Account Mapping here"
                  subtitle="Columns: Account · Statement · LineItem · Classification · Group"
                  icon={IoMapOutline}
                  dragging={mapDrag}
                  loaded={mappingName ? `${pendingMapping.length} rules — ${mappingName}` : ""}
                  accent="#C9A84C"
                  onDragOver={(e) => { e.preventDefault(); setMapDrag(true); }}
                  onDragLeave={() => setMapDrag(false)}
                  onDrop={(e) => { e.preventDefault(); setMapDrag(false); handleMappingFile(e.dataTransfer.files[0]); }}
                  onClick={() => mapRef.current.click()}
                  fileRef={mapRef}
                  onFileChange={(e) => handleMappingFile(e.target.files[0])}
                />

                {/* Mapping preview */}
                {pendingMapping && <MappingPreview mapping={pendingMapping} />}

                {error && <VuiTypography variant="caption" color="error" display="block">⚠ {error}</VuiTypography>}

                {apiStatus && (
                  <VuiBox px={2} py={1} sx={{ borderRadius: "8px", background: apiStatus.startsWith("success") ? "rgba(72,187,120,0.12)" : "rgba(252,129,129,0.12)" }}>
                    <VuiTypography variant="caption" sx={{ color: apiStatus.startsWith("success") ? "#48BB78" : "#FC8181" }}>
                      {apiStatus.startsWith("success") ? "✓ " : "⚠ "}{apiStatus.split(":")[1]}
                    </VuiTypography>
                  </VuiBox>
                )}

                {/* Action buttons */}
                <VuiBox display="flex" flexDirection="column" gap={1.5} mt={1}>
                  <VuiButton color="warning" variant="contained" fullWidth onClick={handleDownloadReport}
                    disabled={downloading || !tbFile || !mapFile}>
                    {downloading ? "Generating..." : "⬇ Download Full Report (Excel)"}
                  </VuiButton>

                  {pendingRows && (
                    <VuiButton color="info" variant="contained" fullWidth onClick={handleViewStatements}>
                      View Live Statements →
                    </VuiButton>
                  )}

                  <VuiBox display="flex" gap={1}>
                    <VuiButton color="secondary" variant="outlined" fullWidth onClick={handleLoadSample}>
                      Load Sample Data
                    </VuiButton>
                    <VuiButton color="secondary" variant="outlined" fullWidth onClick={handleDownloadTemplate}>
                      ⬇ TB Template
                    </VuiButton>
                  </VuiBox>
                </VuiBox>

              </VuiBox>
            </Card>
          </Grid>

          {/* ── Right: How it works ── */}
          <Grid item xs={12} lg={7}>
            <VuiBox display="flex" flexDirection="column" gap={3}>

              {/* How it works */}
              <Card>
                <VuiTypography variant="lg" color="white" fontWeight="bold" mb={3}>How it works</VuiTypography>
                {[
                  { step: "1", color: "#0075FF", title: "Upload Trial Balance", desc: "Export your TB from any ERP — SAP, Oracle, Tally, QuickBooks. Needs Account, Account_Name, Opening, Debit, Credit, Closing columns." },
                  { step: "2", color: "#C9A84C", title: "Upload Mapping File",  desc: "Maps your account codes to BS/P&L line items. Columns: Account · Statement · LineItem · Classification · Group." },
                  { step: "3", color: "#48BB78", title: "View Live Statements", desc: "Instantly see Balance Sheet, P&L and KPIs — calculated in-browser with live preview of your data." },
                  { step: "4", color: "#FC8181", title: "Download Board Report", desc: "Full formatted Excel — Cover page, Balance Sheet, P&L, Notes 1–24, and TB Source in one click." },
                ].map((item) => (
                  <VuiBox key={item.step} display="flex" gap={2} mb={2.5}>
                    <VuiBox sx={{ minWidth: "32px", height: "32px", borderRadius: "50%", background: item.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <VuiTypography variant="button" color="white" fontWeight="bold">{item.step}</VuiTypography>
                    </VuiBox>
                    <VuiBox>
                      <VuiTypography variant="button" color="white" fontWeight="bold" display="block">{item.title}</VuiTypography>
                      <VuiTypography variant="caption" color="text">{item.desc}</VuiTypography>
                    </VuiBox>
                  </VuiBox>
                ))}
              </Card>

              {/* Expected columns */}
              <Card>
                <VuiTypography variant="button" color="white" fontWeight="bold" display="block" mb={2}>
                  Expected TB Columns
                </VuiTypography>
                <VuiBox sx={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                    <thead>
                      <tr style={{ background: "rgba(0,117,255,0.15)" }}>
                        {["Column", "Example", "Required"].map((h) => (
                          <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#A0AEC0", fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { col: "Account",          ex: "1001",              req: true },
                        { col: "Account_Name",     ex: "Cash & Bank",       req: false },
                        { col: "Opening_Balance",  ex: "100,000",           req: false },
                        { col: "Debit",            ex: "500,000",           req: true },
                        { col: "Credit",           ex: "450,000",           req: true },
                        { col: "Closing_Balance",  ex: "150,000",           req: false },
                      ].map((r, i) => (
                        <tr key={r.col} style={{ background: i % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent" }}>
                          <td style={{ padding: "7px 12px", color: "#E2E8F0", fontFamily: "monospace" }}>{r.col}</td>
                          <td style={{ padding: "7px 12px", color: "#A0AEC0" }}>{r.ex}</td>
                          <td style={{ padding: "7px 12px" }}>
                            {r.req
                              ? <IoCheckmarkCircle color="#48BB78" size="14px" />
                              : <IoWarningOutline color="#56577A" size="14px" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </VuiBox>
              </Card>

            </VuiBox>
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UploadTB;
